import React, { memo, useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { Input, Card, CardBody, Select, SelectItem, Button } from '@heroui/react';
import { EnvelopeSimple, At, CaretDown, CaretUp, ClipboardText } from '@phosphor-icons/react';
import { CcBccSearch } from './CcBccSearch';
import { RecipientFields } from './RecipientFields';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { emailEditorModules } from 'shared/config/quillSetup';
import { useMassMailContext } from '../context/MassMailContext';
import { TranslateToolbar } from './TranslateToolbar';
import { PlaceholderPreview } from './PlaceholderPreview';
import { NOREPLY_SENDER_EMAIL, resolveSenderField } from '../utils/senderOptions';
import { AiReplySuggestionPanel } from 'shared/components/email/AiReplySuggestionPanel';
import { replySuggestionToHtml } from 'shared/utils/replySuggestionHtml';
import { useAiReplySuggestion } from 'shared/hooks/useAiReplySuggestion';
import { AiReplySuggestion } from 'shared/api/ai-reply-suggestion';
import * as Sentry from '@sentry/react';

const formats = [
  'header', 'bold', 'italic', 'underline',
  'list', 'bullet', 'color', 'link', 'image'
];

export const EmailComposer: React.FC = memo(() => {
  const { state, dispatch } = useMassMailContext();
  const quillRef = useRef<ReactQuill>(null);
  const [showCcBcc, setShowCcBcc] = useState(false);

  // Convert File/Blob to base64 data URL
  const fileToBase64 = useCallback((file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }, []);

  // Insert image at current cursor position
  const insertImage = useCallback((base64Data: string) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'image', base64Data);
      quill.setSelection(range.index + 1, 0);
    }
  }, []);

  // Handle paste event to capture images
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const items = Array.from(clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length === 0) return;

    // Prevent default paste for images
    e.preventDefault();
    e.stopPropagation();

    for (const item of imageItems) {
      const file = item.getAsFile();
      if (file) {
        try {
          // Check file size (max 5MB for inline images)
          if (file.size > 5 * 1024 * 1024) {
            console.warn('Afbeelding te groot voor inline plaatsing (max 5MB)');
            continue;
          }

          const base64Data = await fileToBase64(file);
          insertImage(base64Data);
        } catch (error) {
          console.error('Fout bij verwerken van geplakte afbeelding:', error);
          Sentry.captureException(error, {
            tags: { feature: 'mass-mail-image-paste' },
            extra: { fileType: file.type, fileSize: file.size }
          });
        }
      }
    }
  }, [fileToBase64, insertImage]);

  // Attach paste listener to quill editor
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const editorElement = quill.root;

    const pasteHandler = (e: Event) => {
      handlePaste(e as ClipboardEvent);
    };

    editorElement.addEventListener('paste', pasteHandler);

    return () => {
      editorElement.removeEventListener('paste', pasteHandler);
    };
  }, [handlePaste]);

  // Quill toolbar configuration - defined inside component to use memoization
  const modules = useMemo(() => emailEditorModules, []);

  const handleSubjectChange = (value: string) => {
    dispatch({ type: 'SET_EMAIL_SUBJECT', payload: value });
  };

  const handleContentChange = (value: string) => {
    dispatch({ type: 'SET_EMAIL_CONTENT', payload: value });
  };

  const handleTranslation = useCallback((translatedHtml: string) => {
    dispatch({ type: 'SET_EMAIL_CONTENT', payload: translatedHtml });
  }, [dispatch]);

  /**
   * Het AI-antwoordvoorstel. De module die de composer opent (helpdesk, reno) geeft
   * de aanroepen mee; staat `state.aiSuggestion` er niet, dan draait deze hook op
   * een generate die nooit aangeroepen wordt en verschijnt het blok ook niet.
   */
  const {
    suggestion: aiSuggestion,
    isGenerating: isGeneratingSuggestion,
    error: aiSuggestionError,
    instruction: aiInstruction,
    setInstruction: setAiInstruction,
    resetInstruction: resetAiInstruction,
    isInstructionModified: isAiInstructionModified,
    requestSuggestion: requestAiSuggestion,
    markUsed: markAiSuggestionUsed,
    dismiss: dismissAiSuggestion,
  } = useAiReplySuggestion({
    generate: state.aiSuggestion?.generate ?? (async () => ({ body: '' })),
    loadPending: state.aiSuggestion?.loadPending,
    close: state.aiSuggestion?.close,
    defaultInstruction: state.aiSuggestion?.defaultInstruction,
    enabled: !!state.aiSuggestion,
  });

  /**
   * Zet het voorstel bovenaan het bericht, vóór wat er al staat.
   *
   * Bovenaan en niet achteraan: bij een antwoord staat het geciteerde blok van de
   * mail al in het veld, en een antwoord hoort daarboven. Het onderwerp blijft
   * ongemoeid — dat heeft de module al opgebouwd met de juiste ticketcode erin,
   * waarop een antwoord straks teruggekoppeld wordt.
   */
  const applyAiSuggestion = useCallback(
    (voorstel: AiReplySuggestion) => {
      dispatch({
        type: 'SET_EMAIL_CONTENT',
        payload: `${replySuggestionToHtml(voorstel.body)}${state.emailContent || ''}`,
      });
      markAiSuggestionUsed();
    },
    [dispatch, state.emailContent, markAiSuggestionUsed],
  );

  const handleSenderEmailChange = (value: string) => {
    if (!value) return; // HeroUI geeft een lege selectie bij deselecteren — afzender blijft verplicht

    dispatch({ type: 'SET_SENDER_EMAIL', payload: value });

    // Add automatic message for noreply emails
    if (value === NOREPLY_SENDER_EMAIL && !state.emailContent.includes("Deze mail is automatisch verstuurd. Gelieve niet te antwoorden.")) {
      const newContent = `${state.emailContent}\n\nDeze mail is automatisch verstuurd. Gelieve niet te antwoorden.`;
      dispatch({ type: 'SET_EMAIL_CONTENT', payload: newContent });
    }
  };

  // Dropdown of vast adres? Standaard vast — enkel de losse mass mail via de
  // plusknop in de navigatiebalk zet `allowSenderSelection`.
  const senderField = useMemo(
    () =>
      resolveSenderField({
        senderEmail: state.senderEmail,
        lockedSenderEmail: state.lockedSenderEmail,
        allowSenderSelection: state.allowSenderSelection,
      }),
    [state.senderEmail, state.lockedSenderEmail, state.allowSenderSelection]
  );


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Inhoud</h3>
      </div>

      <div className="space-y-4">
        {/* Verzenden vanaf. Vanuit een module (helpdesk, reno, incasso, ...) staat het
            adres vast; enkel de losse mass mail laat de gebruiker kiezen — daar is
            de dropdown ook meteen zichtbaar, zonder dat er eerst een template
            gekozen moet worden. */}
        {senderField.editable ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verzenden vanaf <span className="text-red-500">*</span>
            </label>
            <Select
              selectedKeys={[senderField.email]}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0] as string;
                handleSenderEmailChange(selected);
              }}
              disallowEmptySelection
              placeholder="Selecteer een verzendadres..."
              startContent={<At size={16} className="text-gray-400" />}
              size="sm"
              items={senderField.options}
              aria-label="Verzenden vanaf"
            >
              {(option) => (
                <SelectItem key={option.email} textValue={option.email} description={option.description}>
                  {option.email}
                </SelectItem>
              )}
            </Select>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Verzenden vanaf</label>
            <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <At size={16} className="text-gray-400" />
              {senderField.email}
            </div>
          </div>
        )}

        {/* Subject Line */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Onderwerp *
          </label>
          <Input
            value={state.emailSubject}
            onChange={(e) => handleSubjectChange(e.target.value)}
            placeholder="Voer het onderwerp van de e-mail in..."
            startContent={<EnvelopeSimple size={16} className="text-gray-400" />}
            size="sm"
            isRequired
          />
        </div>

        {/* Ontvangers. Bij één gezamenlijke mail (reno) staan Aan/CC/BCC hier altijd open
            en delen ze hun gegevens met de contactenlijst; anders blijft het de
            inklapbare, losse CC/BCC-aanvulling van de gewone mass mail. */}
        {state.enableRecipientTypes ? (
          <RecipientFields />
        ) : (
          <div>
            <button
              type="button"
              onClick={() => setShowCcBcc(prev => !prev)}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              {showCcBcc ? <CaretUp size={14} /> : <CaretDown size={14} />}
              CC / BCC toevoegen
              {(state.ccContacts.length > 0 || state.bccContacts.length > 0) && (
                <span className="ml-1 text-xs font-medium text-blue-600">
                  ({state.ccContacts.length + state.bccContacts.length})
                </span>
              )}
            </button>

            {showCcBcc && (
              <div className="mt-3 space-y-3">
                <CcBccSearch
                  label="CC"
                  contacts={state.ccContacts}
                  onChange={(contacts) => dispatch({ type: 'SET_CC_CONTACTS', payload: contacts })}
                />
                <CcBccSearch
                  label="BCC"
                  contacts={state.bccContacts}
                  onChange={(contacts) => dispatch({ type: 'SET_BCC_CONTACTS', payload: contacts })}
                />
              </div>
            )}
          </div>
        )}

        {/* Translation Toolbar */}
        <TranslateToolbar
          emailContent={state.emailContent}
          onTranslate={handleTranslation}
          disabled={!state.emailContent.trim()}
        />

        {/* Placeholder value preview — verifies {totalDebt}, {buildingName}, etc. resolve correctly */}
        <PlaceholderPreview />

        {/* AI-antwoordvoorstel. Staat er alleen wanneer de module die meegeeft
            (helpdesk- en renovatietickets): een losse mass mail heeft geen
            mailverloop om op te antwoorden. Er wordt nooit iets verstuurd — het
            voorstel komt in het tekstvak eronder. */}
        {state.aiSuggestion && (
          <AiReplySuggestionPanel
            suggestion={aiSuggestion}
            isGenerating={isGeneratingSuggestion}
            error={aiSuggestionError}
            instruction={aiInstruction}
            onInstructionChange={setAiInstruction}
            onResetInstruction={resetAiInstruction}
            canResetInstruction={isAiInstructionModified}
            onGenerate={requestAiSuggestion}
            onApply={applyAiSuggestion}
            onDismiss={dismissAiSuggestion}
          />
        )}

        {/* Email Content */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Tekst *
            </label>
            {state.clipboardBlock && (
              <Button
                size="sm"
                variant="light"
                onPress={() => dispatch({ type: 'SET_EMAIL_CONTENT', payload: (state.emailContent || '') + state.clipboardBlock })}
                startContent={<ClipboardText size={15} />}
                className="text-moss-primary hover:bg-moss-primary/10 text-xs"
              >
                Contact & facturatiegegevens invoegen
              </Button>
            )}
          </div>
          <Card className="w-full">
            <CardBody className="p-0">
              <ReactQuill
                ref={quillRef}
                value={state.emailContent}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                theme="snow"
                placeholder="Schrijf hier de inhoud van uw e-mail... (je kunt ook afbeeldingen plakken)"
                style={{
                  minHeight: '200px',
                }}
              />
            </CardBody>
          </Card>
        </div>

        {/* Basic Validation */}
        {(!state.emailSubject.trim() || !state.emailContent.trim()) && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <span className="font-medium">Vereist:</span> Vul zowel het onderwerp als de inhoud in om door te kunnen gaan.
            </p>
          </div>
        )}
      </div>
    </div>
  );
});
